import IconCheck from '@mui/icons-material/Check'
import IconClose from '@mui/icons-material/Close'
import FiltersIcon from '@mui/icons-material/Tune'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Tooltip from '@mui/material/Tooltip'
import CollapsibleTextInput from 'components/CollapsibleTextInput/CollapsibleTextInput'
import { ARTICLE_TYPE, PHOTO_TYPE, STATUS_TYPE, VIDEO_TYPE } from 'interfaces'
import { POST_TYPES } from 'interfaces/Content/ContentType'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import { capitalize, ContentType } from 'shared'
import HistoryRangePicker, { HistoryPostsTimeRange, getRangeStartByKey } from '../HistoryRangePicker/HistoryRangePicker'
import styles from './PostsFilter.pcss'

const PAPER_ELEVATION = 8

export const POST_VIEW_OPTIONS = [{
  label: <FormattedMessage id="app.nav.publishing-planned-all" />,
  value: 'posts/planned/all'
}, {
  label: <FormattedMessage id="app.nav.publishing-planned-queued" />,
  value: 'posts/planned/queued'
}, {
  label: <FormattedMessage id="app.nav.publishing-planned-queued-buckets" />,
  value: 'posts/planned/buckets'
}, {
  label: <FormattedMessage id="app.nav.publishing-planned-queued-queue" />,
  value: 'posts/planned/no-bucket'
}, {
  label: <FormattedMessage id="app.nav.publishing-pending-posts" />,
  value: 'posts/planned/queued/pending'
}, {
  label: <FormattedMessage id="app.nav.publishing-planned-scheduled" />,
  value: 'posts/planned/scheduled'
}]

const POST_FAVORITES_OPTIONS = [{
  label: <FormattedMessage id="post.posted-filter.favorites.only" />,
  value: 'favorites'
}, {
  label: <FormattedMessage id="post.posted-filter.favorites.non-favorites" />,
  value: 'non-favorites'
}, {
  label: <FormattedMessage id="post.posted-filter.favorites.all" />,
  value: 'all'
}]

const rangeOptions = [{
  label: <FormattedMessage id="post.posted.time-range.latest" />,
  value: 'latest'
}, {
  label: <FormattedMessage id="post.posted.time-range.week" />,
  value: 'week'
}, {
  label: <FormattedMessage id="post.posted.time-range.month" />,
  value: 'month'
}, {
  label: <FormattedMessage id="post.posted.time-range.year" />,
  value: 'year'

}, {
  label: <FormattedMessage id="post.posted.time-range.all" />,
  value: 'all'
}, {
  label: <FormattedMessage id="post.posted.time-range.custom" />,
  value: 'custom'
}]

const contentTypeOptions = [{
  label: 'Images',
  value: PHOTO_TYPE
}, {
  label: 'Videos',
  value: VIDEO_TYPE
}, {
  label: 'Articles/Links',
  value: ARTICLE_TYPE
}, {
  label: 'Text-only',
  value: STATUS_TYPE
}]

export type FavoritesFilter = 'all' | 'favorites' | 'non-favorites'

interface PostsFilterProps {
  activeTypes: ContentType[]
  className?: string
  draftsView?: boolean
  postsView?: string
  range?: HistoryPostsTimeRange
  favoritesFilter?: FavoritesFilter
  archivedPostsToggle?: {
    checked: boolean
    onToggle: () => void
  }
  filterDisabled?: boolean
  filterDisabledMessage?: React.ReactNode
  onRangeChange?: (r: HistoryPostsTimeRange, start?: string, end?: string) => void
  onFilter: (query: string, types: ContentType[], searchInSummary?: boolean, favType?: FavoritesFilter) => void
  onPostViewChange?: (type: string) => void
}

export function PostsFilter(props: PostsFilterProps) {
  const intl = useIntl()
  const location = useLocation()
  const [selectedTypes, setSelectedTypes] = React.useState<ContentType[]>(props.activeTypes)
  const [popupAnchor, setPopupAnchor] = React.useState<HTMLElement | null>(null)
  const [searchInSummary, setSearchInSummary] = React.useState(true)
  const [rangeDialogOpen, setRangeDialogOpen] = React.useState(false)
  const [showClear, setShowClear] = React.useState(false)
  const btnRef = React.useRef<HTMLButtonElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const { archivedPostsToggle, favoritesFilter, onFilter } = props

  React.useEffect(() => {
    if (location.state?.postsSearchQuery && inputRef.current) {
      inputRef.current.value = location.state.postsSearchQuery
      setShowClear(true)
    }
  }, [location.state?.postsSearchQuery])

  React.useEffect(() => {
    setSelectedTypes(props.activeTypes)
  }, [props.activeTypes])

  const onSummarySearchChange = () => {
    const q = inputRef.current?.value || ''
    onFilter(q, selectedTypes, !searchInSummary)
    setSearchInSummary(!searchInSummary)
  }

  const openFiltersPopup = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setPopupAnchor(btnRef.current || null)
  }

  const closeFiltersPopup = () => {
    setPopupAnchor(null)
    setSelectedTypes(props.activeTypes)
  }

  const onToggleType = (type: ContentType) => {
    const nextTypes = selectedTypes.includes(type) ? selectedTypes.filter(t => t !== type) : [...selectedTypes, type]
    setSelectedTypes(nextTypes)
    const q = inputRef.current?.value || ''
    onFilter(q, nextTypes, searchInSummary)
  }

  const onPostTypeChange = (value: string) => {
    if (value !== props.postsView) {
      props.onPostViewChange?.(value)
      closeFiltersPopup()
    }
  }

  const onSearch = React.useCallback((value: string) => {
    onFilter(value, selectedTypes, searchInSummary)
    setShowClear(true)
  }, [onFilter, selectedTypes, searchInSummary])

  const clearSearch = () => {
    props.onFilter('', POST_TYPES)
    setShowClear(false)
    setSelectedTypes(POST_TYPES)

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const resetFilters = () => {
    clearSearch()
    if (props.onPostViewChange) {
      props.onPostViewChange('posts/planned/all')
    }
    if (props.onRangeChange) {
      props.onRangeChange('all')
    }
    if (archivedPostsToggle?.checked) {
      archivedPostsToggle.onToggle()
    }
  }

  const closeRangeDialog = () => {
    setRangeDialogOpen(false)
  }

  const onSelectedRangeChange = (r: HistoryPostsTimeRange, startDate?: string, endDate?: string) => {
    if (startDate && endDate) {
      props.onRangeChange?.(r, startDate, endDate)
    } else if (r === 'custom' && !startDate && !endDate) {
      setRangeDialogOpen(true)
    } else {
      setRangeDialogOpen(false)
      if (r === 'all') {
        props.onRangeChange?.(r)
      } else {
        const start = getRangeStartByKey(r)?.toISOString()
        const end = new Date().toISOString()
        props.onRangeChange?.(r, start, end)
      }
    }
    closeFiltersPopup()
  }

  const contentTypeLabelsMap = contentTypeOptions.reduce((acc, { label, value }) => {
    acc[value] = label
    return acc
  }, {} as Record<string, string>)

  const btnLabel = React.useMemo(() => {
    const hasActiveTypes = props.activeTypes.length !== contentTypeOptions.length
    const hasActiveRange = props.range && props.range !== 'all'

    if (!hasActiveTypes && !hasActiveRange) {
      return 'Filter'
    }

    const typeLabels = hasActiveTypes
      ? props.activeTypes.map(value => capitalize(contentTypeLabelsMap[value])).join('/')
      : ''
    const rangeLabel = hasActiveRange
      ? intl.formatMessage({ id: `post.posted.time-range.${props.range}` })
      : ''

    return `Filter: ${[rangeLabel, typeLabels].filter(Boolean).join('/')}`
  }, [props.activeTypes, props.range, intl, contentTypeLabelsMap])

  const onFavChange = (value: string) => {
    if (value !== favoritesFilter) {
      onFilter(inputRef.current?.value || '', selectedTypes, searchInSummary, value as FavoritesFilter)
      closeFiltersPopup()
    }
  }

  const withRangeSelect = props.range && props.onRangeChange

  const isFilterActive = React.useMemo(() => {
    const hasActiveTypes = props.activeTypes.length !== contentTypeOptions.length
    const hasActiveRange = props.range && props.range !== 'all'
    const hasFavoritesFilter = favoritesFilter && favoritesFilter !== 'all'
    return hasActiveTypes || hasActiveRange || props.archivedPostsToggle?.checked || hasFavoritesFilter
  }, [props.activeTypes, props.range, props.archivedPostsToggle, favoritesFilter])

  const onToggleArchived = () => {
    archivedPostsToggle?.onToggle()
    closeFiltersPopup()
  }

  return (
    <div className={`${styles.container} ${props.className || ''}`}>
      <CollapsibleTextInput
        ref={inputRef}
        changeOnEnter
        initialActive
        disableCollapse
        type="search"
        placeholder={intl.formatMessage({
          id: props.draftsView ? 'post.search-drafts-placeholder' : 'post.search-posts-placeholder'
        })}
        className={`${styles.search} ${showClear ? styles['search-active'] : ''}`}
        activeClassName={styles['search-active']}
        actionButton={(
          <div className={styles['input-actions']}>
            {showClear && (
              <IconButton
                size="small"
                className={styles['btn-clear']}
                onClick={clearSearch}
              >
                <IconClose className={styles['icon-clear']} />
              </IconButton>
            )}
            <Tooltip title={props.filterDisabled ? (props.filterDisabledMessage || '') : btnLabel}>
              <div>
                <IconButton
                  ref={btnRef}
                  size="small"
                  aria-label={btnLabel}
                  className={`${styles['btn-filter']} ${isFilterActive ? styles['btn-filter-active'] : ''}`}
                  disabled={props.filterDisabled}
                  onClick={openFiltersPopup}
                >
                  <FiltersIcon className={styles['icon-filters']} />
                </IconButton>
              </div>
            </Tooltip>
          </div>
        )}
        onValueChanged={onSearch}
      />
      <Popper
        open={Boolean(popupAnchor)}
        anchorEl={popupAnchor}
        className={styles.popup}
        placement="bottom-end"
      >
        <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={closeFiltersPopup}>
          <Paper className={styles.wrapper} elevation={PAPER_ELEVATION}>
            <div className={styles['popup-content']}>
              {props.postsView && props.onPostViewChange && (
                <ViewOptionSelect
                  label={<FormattedMessage id="post.filters.post-type" />}
                  values={[props.postsView || '']}
                  options={POST_VIEW_OPTIONS}
                  onToggleValue={onPostTypeChange}
                />
              )}
              {withRangeSelect && (
                <ViewOptionSelect
                  label={<FormattedMessage id="post.filters.posted-range" />}
                  values={[props.range || '']}
                  options={rangeOptions}
                  onToggleValue={onSelectedRangeChange}
                />
              )}
              <ViewOptionSelect
                label={<FormattedMessage id="post.filters.content-type" />}
                values={selectedTypes}
                options={contentTypeOptions}
                onToggleValue={onToggleType}
              />
              {favoritesFilter && (
                <ViewOptionSelect
                  label={<FormattedMessage id="post.filters.favorites" />}
                  values={[favoritesFilter || '']}
                  options={POST_FAVORITES_OPTIONS}
                  onToggleValue={onFavChange}
                />
              )}
              {archivedPostsToggle && (
                <ViewOptionSelect
                  label={<FormattedMessage id="post.filters.archived" />}
                  values={archivedPostsToggle!.checked ? ['archived'] : []}
                  options={[{ label: 'Archived', value: 'archived' }]}
                  onToggleValue={onToggleArchived}
                />
              )}
              <div className={styles.bottom}>
                {/* <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={searchInSummary}
                      disabled={!selectedTypes.includes(ARTICLE_TYPE)}
                      onChange={onSummarySearchChange}
                    />
                  )}
                  classes={{ label: styles['cb-label'] }}
                  label={intl.formatMessage({ id: 'post.search-posts.search-in-summary' })}
                /> */}
                <div>
                  <Button className={styles['btn-reset']} onClick={resetFilters}>
                    <FormattedMessage id="actions.reset" />
                  </Button>
                </div>
              </div>
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
      {withRangeSelect && (
        <HistoryRangePicker
          value={props.range as HistoryPostsTimeRange}
          datePickerMode
          dialogOpen={rangeDialogOpen}
          onClose={closeRangeDialog}
          onValueChange={onSelectedRangeChange}
        />
      )}
    </div>
  )
}

interface ViewOptionSelectProps {
  label: React.ReactNode
  options: Array<{ label: React.ReactNode, value: string, disabled?: boolean }>
  values: string[]
  onToggleValue: (value: string) => void
}

export function ViewOptionSelect({ label, options, values, onToggleValue }: ViewOptionSelectProps) {
  const onClick = (value: string, disabled?: boolean) => () => {
    if (!disabled) {
      onToggleValue(value)
    }
  }

  return (
    <div className={styles.option}>
      <div className={styles.label}>
        {label}:
      </div>
      <div className={styles.switch}>
        {options.map(o => {
          const active = values.includes(o.value)
          return (
            <div
              key={o.value}
              data-testid="posts-filter-option"
              className={`${active ? styles['tab-active'] : ''} ${o.disabled ? styles.disabled : ''}`}
              onClick={onClick(o.value, o.disabled)}
            >
              {o.label}
              {active && <IconCheck color="primary" fontSize="small" className={styles.icon} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PostsFilter
